import { deleteField } from 'firebase/firestore';
import { ArrowLeft, History } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Field, FormSection, SelectInput, ToggleField } from '@/components/Admin/AdminFormFields';
import { AutosaveRichTextInput } from '@/components/Admin/Autosave/AutosaveRichTextInput';
import { AutosaveTextInput } from '@/components/Admin/Autosave/AutosaveTextInput';
import { AvailabilityClosuresField } from '@/components/Admin/AvailabilityClosuresField';
import { AvailabilitySlotsField } from '@/components/Admin/AvailabilitySlotsField';
import { GalleryImagesField } from '@/components/Admin/GalleryImagesField';
import { HeroImageField } from '@/components/Admin/HeroImageField';
import { HeroVideoField } from '@/components/Admin/HeroVideoField';
import { OptionalServicesField } from '@/components/Admin/OptionalServicesField';
import { SlugField } from '@/components/Admin/SlugField';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  createExperience,
  getExperience,
  isExperienceCodeTaken,
  isExperienceIdTaken,
} from '@/data/experiencesRepo';
import { DocumentIdTakenError } from '@/data/util_documentIds';
import { useAutosaveExperience } from '@/hooks/autosave/useAutosaveExperience';
import { useSaveStack } from '@/hooks/autosave/util_saveStack';
import { TopWhiteBarEditControls } from '@/hooks/autosave/TopWhiteBarEditControls';
import { HistoryPanel } from '@/hooks/autosave/HistoryPanel';
import { UndoRedoButtons } from '@/hooks/autosave/UndoRedoButtons';
import { undoRedoStorageKey, useUndoRedoStack } from '@/hooks/autosave/useUndoRedoStack';
import {
  countFormatter,
  describeChange,
  labelFormatter,
  type ValueFormatterType,
} from '@/hooks/autosave/util_historyMessage';
import { useCollections } from '@/hooks/useCollections';
import { useExperienceClosures, useSuppliers } from '@/hooks/useSuppliers';
import { normaliseShortCode, shortCodeLength, validateShortCode } from '@/types/types_code';
import {
  coll_experiencesHistory,
  experienceBookingTypeLabels,
  experienceLayoutLabels,
  experienceRegionLabels,
  experienceStatusLabels,
  isCalendarBookingType,
  makeEmptyExperience,
  type ExperienceBookingType,
  type ExperienceLayoutType,
  type ExperienceOptionalServiceType,
  type ExperienceRegionType,
  type ExperienceStatusType,
  type ExperienceType,
} from '@/types/types_experience';
import { normaliseSupplierClosures } from '@/types/types_supplier';
import { formatJpy, todayCalendarDate } from '@/util/util_format';
import { slugify, validateSlugForCreate } from '@/util/util_slug';

/** How a service reads in a log line before it has been given a name. */
function serviceName(service: ExperienceOptionalServiceType): string {
  return service.label ? `'${service.label}'` : 'with no name yet';
}

/**
 * The history line for editing one service row. Named per field rather than as
 * "changed a service", because the panel is read to find out what someone did to
 * the price of the shuttle.
 */
function describeServiceEdit(service: ExperienceOptionalServiceType, changed: string[]): string {
  if (changed.length === 1) {
    if (changed[0] === 'label') {
      return service.label
        ? `Renamed an optional service to '${service.label}'`
        : 'Cleared the name of an optional service';
    }
    if (changed[0] === 'price') {
      return `Priced the optional service ${serviceName(service)} at ${formatJpy(service.price)}`;
    }
    if (changed[0] === 'commissionable') {
      return service.commissionable
        ? `Made the optional service ${serviceName(service)} commissionable`
        : `Made the optional service ${serviceName(service)} non-commissionable`;
    }
  }
  return `Changed the optional service ${serviceName(service)}`;
}

interface PageExperienceEditProps {
  mode: 'new' | 'edit';
}

/**
 * Create / edit form for a single experience.
 *
 * In `edit` mode the form autosaves each field to Firestore on commit (blur),
 * showing status in a sticky top bar. In `new` mode there's nothing to autosave
 * until the document exists, so it keeps a Create button and persists on submit.
 */
export function PageExperienceEdit({ mode }: PageExperienceEditProps) {
  const { experienceId } = useParams<{ experienceId: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<ExperienceType>(() => makeEmptyExperience());
  // Mutable source of truth for reads, so save logic never races React state.
  const formRef = useRef(form);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Why the Create button refused, shown above it. Only reachable in `new` mode:
  // an edit reports its problems on the field itself, since it saves per field.
  const [createError, setCreateError] = useState<string | null>(null);
  // The web address field's own complaint, shown under it. `new` mode only: the
  // id of an existing experience is fixed, so the field is not rendered at all.
  const [slugError, setSlugError] = useState<string | null>(null);

  const { collections } = useCollections();
  const { suppliers } = useSuppliers();
  // The supplier's absences, folded into this experience's own. Everything on
  // this screen that asks a closure question asks it of these — the slot
  // calendar refuses a time on a day the artisan is away whether that day came
  // from here or from the supplier.
  const { closures, supplier } = useExperienceClosures(form);
  const saveStack = useSaveStack();
  const autosave = useAutosaveExperience(mode === 'edit' ? experienceId : undefined, saveStack);
  const undoRedo = useUndoRedoStack();
  const { loadFor: loadUndoRedoFor } = undoRedo;

  useEffect(() => {
    if (mode !== 'edit' || !experienceId) return;
    let cancelled = false;
    getExperience(experienceId)
      .then((exp) => {
        if (cancelled) return;
        if (exp) {
          formRef.current = exp;
          setForm(exp);
          // Restores this document's persisted undo stack (and discards it if the
          // record has moved on since it was written).
          loadUndoRedoFor(
            undoRedoStorageKey('experience', experienceId),
            (field) => formRef.current[field as keyof ExperienceType],
          );
        } else {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, experienceId, loadUndoRedoFor]);

  // Write a field value: reflect locally and, in edit mode, autosave it along
  // with a persisted history step. Shared by direct edits and by undo/redo.
  // History is built synchronously (and formRef updated immediately) so rapid
  // successive edits each see the incremented history and never collide.
  function applyChange(field: string, value: unknown, userAction: string, isUndoStep = false) {
    if (mode === 'edit') {
      const { newHistory } = autosave(userAction, { [field]: value }, formRef.current.history, isUndoStep);
      formRef.current = { ...formRef.current, [field]: value, history: newHistory };
      setForm(formRef.current);
    } else {
      formRef.current = { ...formRef.current, [field]: value };
      setForm(formRef.current);
    }
  }

  // Collections are referenced by id, which means nothing in a log entry, so
  // resolve the name. Falls back to the id if the collection has since gone.
  const collectionNameFormatter: ValueFormatterType = (value) =>
    collections.find((c) => c.id === value)?.name ?? undefined;

  // Suppliers are referenced by id too, and read the same way in a log line.
  const supplierNameFormatter: ValueFormatterType = (value) =>
    suppliers.find((sup) => sup.id === value)?.name ?? undefined;

  // A user edit: apply it and (edit mode) record it on the undo stack.
  //
  // `label` names the field ("base price"); the message around it is composed by
  // describeChange, so every field reads the same way and undo/redo can restate
  // itself in terms of the value being restored. `format` renders values that
  // shouldn't be shown raw — a select's code, a price, an unreadable URL.
  function commit<K extends keyof ExperienceType>(
    key: K,
    value: ExperienceType[K],
    label: string,
    format?: ValueFormatterType,
  ) {
    const prevValue = formRef.current[key];
    if (Object.is(prevValue, value)) return;
    const describe = (v: unknown) => describeChange(label, v, format);
    applyChange(key, value, describe(value));
    if (mode === 'edit') {
      undoRedo.record({
        field: key,
        prevValue,
        nextValue: value,
        undoMessage: describe(prevValue),
        redoMessage: describe(value),
      });
    }
  }

  // ----- Optional services -----
  //
  // Stored as a map, so an edit names the row it touched —
  // `optionalServices.{id}.price` rather than a rewritten list. Two admins on
  // the same experience keep each other's rows, and a poor connection sends one
  // field instead of the whole catalogue.
  //
  // Undo still moves the list as a whole. "Put it back how it was" is about the
  // list rather than one row, and the undo stack stores whole field values
  // anyway, so the step recorded here holds both maps and replays through
  // `applyChange`, which writes the field entire.
  const describeServices = (value: unknown) =>
    describeChange('optional services', value, countFormatter('service'));

  function saveServices(
    next: Record<string, ExperienceOptionalServiceType>,
    addressed: Record<string, unknown>,
    userAction: string,
  ) {
    const prevValue = formRef.current.optionalServices;

    if (mode !== 'edit') {
      formRef.current = { ...formRef.current, optionalServices: next };
      setForm(formRef.current);
      return;
    }

    const { newHistory } = autosave(userAction, addressed, formRef.current.history);
    formRef.current = { ...formRef.current, optionalServices: next, history: newHistory };
    setForm(formRef.current);

    undoRedo.record({
      field: 'optionalServices',
      prevValue,
      nextValue: next,
      undoMessage: describeServices(prevValue),
      redoMessage: describeServices(next),
    });
  }

  function patchService(
    service: ExperienceOptionalServiceType,
    changes: Partial<ExperienceOptionalServiceType>,
  ) {
    // Against the live row rather than the one the input was rendered from: a
    // text field commits on blur, by which time the checkbox beside it may
    // already have written. Gone means it was removed while the field still had
    // focus, and there is nothing left to patch.
    const live = formRef.current.optionalServices[service.id];
    if (!live) return;

    // Only the keys that actually moved, so a field committed on blur without
    // being touched writes nothing and leaves no history step behind.
    const moved = Object.entries(changes).filter(
      ([key, value]) => !Object.is(live[key as keyof ExperienceOptionalServiceType], value),
    );
    if (moved.length === 0) return;

    const updated = { ...live, ...changes };
    saveServices(
      { ...formRef.current.optionalServices, [service.id]: updated },
      Object.fromEntries(moved.map(([key, value]) => [`optionalServices.${service.id}.${key}`, value])),
      describeServiceEdit(updated, moved.map(([key]) => key)),
    );
  }

  function addService(service: ExperienceOptionalServiceType) {
    saveServices(
      { ...formRef.current.optionalServices, [service.id]: service },
      { [`optionalServices.${service.id}`]: service },
      'Added an optional service',
    );
  }

  function removeService(service: ExperienceOptionalServiceType) {
    const next = { ...formRef.current.optionalServices };
    delete next[service.id];
    saveServices(
      next,
      { [`optionalServices.${service.id}`]: deleteField() },
      `Removed the optional service ${serviceName(service)}`,
    );
  }

  // Why this code cannot be used, or null. Shape first — a malformed code is
  // worth saying without a round trip — then whether another experience holds
  // it, which only Firestore can answer. `experienceId` is excluded so a record
  // never clashes with the code it already has; in `new` mode nothing exists to
  // exclude yet.
  async function validateCode(value: string): Promise<string | null> {
    const shapeError = validateShortCode(value);
    if (shapeError) return shapeError;
    const code = normaliseShortCode(value);
    if (!code) return null;
    try {
      return (await isExperienceCodeTaken(code, experienceId))
        ? `Code ${code} is already used by another experience.`
        : null;
    } catch (e: unknown) {
      // Better to say the check didn't happen than to save a code over one that
      // might already exist.
      console.error('Experience code check failed', e);
      return 'Could not check whether this code is free. Try again.';
    }
  }

  // The web address, written straight onto the form. Stable so that SlugField's
  // effect — which fills the field in from the name — does not re-run per render.
  const setSlug = useCallback((slug: string) => {
    formRef.current = { ...formRef.current, id: slug };
    setForm(formRef.current);
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setCreateError(null);
    // Both checked again here, not only on blur: the fields validate what was in
    // them when the user left them, and the catalogue may have moved on since.
    const codeError = await validateCode(formRef.current.code);
    if (codeError) {
      setCreateError(codeError);
      setSaving(false);
      return;
    }
    const id = slugify(formRef.current.id || formRef.current.name);
    const idError = await validateSlugForCreate(id, isExperienceIdTaken, 'experience');
    if (idError) {
      setSlugError(idError);
      setCreateError('Check the highlighted field above.');
      setSaving(false);
      return;
    }
    const toSave: ExperienceType = {
      ...formRef.current,
      code: normaliseShortCode(formRef.current.code),
      id,
    };
    try {
      await createExperience(toSave);
    } catch (e: unknown) {
      // The id was free a moment ago and is not any more — somebody else created
      // it in between, and the transaction refused rather than writing over them.
      if (e instanceof DocumentIdTakenError) {
        setSlugError('That web address was taken while this form was open. Choose a different one.');
        setCreateError('Check the highlighted field above.');
      } else {
        console.error('Create experience failed', e);
        setCreateError('The experience could not be created. Try again.');
      }
      setSaving(false);
      return;
    }
    setSaving(false);
    navigate('/admin/experiences');
  }

  if (loading) {
    return <div className='tw:py-16 tw:text-center tw:text-muted-foreground'>Loading…</div>;
  }

  if (notFound) {
    return (
      <div className='tw:py-16 tw:text-center'>
        <p className='tw:text-lg'>Experience not found.</p>
        <Link to='/admin/experiences' className='tw:mt-4 tw:inline-block tw:text-primary tw:underline'>
          Back to experiences
        </Link>
      </div>
    );
  }

  const isEdit = mode === 'edit';

  // Field editors — text fields commit on blur; selects/toggles/media commit immediately.
  const fields = (
    <div className='tw:space-y-8'>
      {/* Basics */}
      <FormSection title='Basics'>
        <Field label='Code'>
          <AutosaveTextInput
            currentValue={form.code}
            maxLength={shortCodeLength}
            normalise={normaliseShortCode}
            validate={validateCode}
            className='tw:w-24 tw:uppercase'
            onCommitChange={(v) => commit('code', v, 'code')}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            The two letters that stand for this experience inside a tour request code, must be unique.
          </p>
        </Field>
        <Field label='Experience Name' required className='tw:sm:col-span-2'>
          <AutosaveTextInput
            currentValue={form.name}
            onCommitChange={(v) => commit('name', v, 'name')}
          />
        </Field>
        {!isEdit && (
          <SlugField
            label='Web Address'
            name={form.name}
            value={form.id}
            onChange={setSlug}
            isTaken={isExperienceIdTaken}
            pathPrefix='/experiences/'
            entityLabel='experience'
            error={slugError}
            onErrorChange={setSlugError}
          />
        )}
        <Field label='Subtitle' className='tw:sm:col-span-2'>
          <AutosaveTextInput
            currentValue={form.subtitle}
            onCommitChange={(v) => commit('subtitle', v, 'subtitle')}
          />
        </Field>

        <Field label='Collection'>
          <Select
            value={form.collectionId ?? 'none'}
            onValueChange={(v) => commit('collectionId', v === 'none' ? null : v, 'collection', collectionNameFormatter)}
          >
            <SelectTrigger>
              <SelectValue placeholder='Standalone (no collection)' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='none'>Standalone (no collection)</SelectItem>
              {collections.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label='Region' required>
          <SelectInput
            value={form.region}
            options={experienceRegionLabels}
            onValueChange={(v) => commit('region', v as ExperienceRegionType, 'region', labelFormatter(experienceRegionLabels))}
          />
        </Field>
        <Field label='Venue Name'>
          <AutosaveTextInput
            currentValue={form.venueName}
            placeholder='Kaikaro Teahouse'
            onCommitChange={(v) => commit('venueName', v, 'venue name')}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            Shown wherever this experience names where it happens &mdash; the public page header, the
            catalogue cards, and the guest&rsquo;s confirmation sheet. Leave blank for an experience with no
            fixed venue.
          </p>
        </Field>
        <Field label='Venue Address'>
          <AutosaveTextInput
            multiline
            rows={3}
            currentValue={form.venueAddress}
            placeholder={'Kaikaro Teahouse\n1-2-3 Higashiyama\nKyoto 605-0862'}
            onCommitChange={(v) => commit('venueAddress', v, 'venue address')}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            Printed on the guest&rsquo;s confirmation sheet, as they would show it to a taxi driver.
            One line per line, as it should print.
          </p>
        </Field>
        <Field label='Google Maps Link' className='tw:sm:col-span-2'>
          {/* Formatted to nothing in the history line, as the embed URL is: a
              maps link is a wall of characters that says nothing in an undo
              message. */}
          <AutosaveTextInput
            currentValue={form.venueMapUrl}
            placeholder='https://maps.app.goo.gl/…'
            onCommitChange={(v) => commit('venueMapUrl', v, 'venue map link', () => null)}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            The Google Maps &ldquo;Share&rdquo; link for the venue. Shows up on the confirmation PDF.
          </p>
        </Field>

        <Field label='Google Maps Embed URL' className='tw:sm:col-span-2'>
          <AutosaveTextInput
            currentValue={form.googleMapsEmbedUrl}
            placeholder='https://www.google.com/maps/embed?…'
            onCommitChange={(v) => commit('googleMapsEmbedUrl', v, 'map URL', () => null)}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            The Google Maps &ldquo;Embed a map&rdquo; link for the venue. Shows up on the public page.
          </p>
        </Field>

        <Field label='Page Layout Format'>
          <SelectInput
            value={form.layoutFormat}
            options={experienceLayoutLabels}
            onValueChange={(v) => commit('layoutFormat', v as ExperienceLayoutType, 'layout', labelFormatter(experienceLayoutLabels))}
          />
        </Field>
        <Field label='Status'>
          <SelectInput
            value={form.status}
            options={experienceStatusLabels}
            onValueChange={(v) => commit('status', v as ExperienceStatusType, 'status', labelFormatter(experienceStatusLabels))}
          />
        </Field>

        <Field label='Featured Order (home page)'>
          <AutosaveTextInput
            numeric
            currentValue={form.featuredOrder?.toString() ?? ''}
            placeholder='Not featured'
            onCommitChange={(v) =>
              commit('featuredOrder', v === '' ? null : Number(v), 'featured order')
            }
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            Set a number to show this experience in the home page&rsquo;s Featured Experiences section, lowest
            first. Leave blank to keep it off the home page.
          </p>
        </Field>
      </FormSection>

      {/* Pricing & booking */}
      <FormSection title='Pricing & Booking'>
        <Field label='Base Price (JPY)'>
          <AutosaveTextInput
            numeric
            currentValue={form.basePrice.toString()}
            onCommitChange={(v) => commit('basePrice', Number(v) || 0, 'base price', (n) => formatJpy(n as number))}
          />
        </Field>
        <Field label='Booking Type'>
          <SelectInput
            value={form.bookingType}
            options={experienceBookingTypeLabels}
            onValueChange={(v) => commit('bookingType', v as ExperienceBookingType, 'booking type', labelFormatter(experienceBookingTypeLabels))}
          />
        </Field>
        <Field label='Min Guests'>
          <AutosaveTextInput
            numeric
            currentValue={form.minGuests?.toString() ?? ''}
            onCommitChange={(v) => commit('minGuests', v === '' ? null : Number(v), 'min guests')}
          />
        </Field>
        <Field label='Max Guests'>
          <AutosaveTextInput
            numeric
            currentValue={form.maxGuests?.toString() ?? ''}
            onCommitChange={(v) => commit('maxGuests', v === '' ? null : Number(v), 'max guests')}
          />
        </Field>

        <ToggleField
          label='Commission Available'
          checked={form.commissionEnabled}
          onCheckedChange={(v) => commit('commissionEnabled', v, 'commission')}
        />
        {form.commissionEnabled && (
          <Field label='Commission Percentage (%)'>
            <AutosaveTextInput
              numeric
              currentValue={form.commissionPercent.toString()}
              onCommitChange={(v) => commit('commissionPercent', Number(v) || 0, 'commission', (n) => `${n as number}%`)}
            />
          </Field>
        )}
        <ToggleField
          label='FAM Trip Available'
          checked={form.famEnabled}
          onCheckedChange={(v) => commit('famEnabled', v, 'FAM trip')}
        />
        {/* Whole days of clear notice, counted back from midnight beginning the
            booked day in JST: seven means free until midnight starting a week
            before, so the seven days up to and including the booked one are
            chargeable. Zero is the most generous setting rather than the
            strictest — free until midnight beginning the day itself. Changing it
            only affects bookings confirmed from now on — an existing booking
            keeps the window it was confirmed under. */}
        <Field label='Cancellation Window (days before)'>
          <AutosaveTextInput
            numeric
            currentValue={form.freeCancellationDays.toString()}
            onCommitChange={(v) =>
              commit(
                'freeCancellationDays',
                Number(v) || 0,
                'cancellation window',
                (n) => `${n as number} days`,
              )}
          />
        </Field>

        {/* Priced extras a partner may ask for with this experience. In this card
            rather than under Content because that is what they are: rows on the
            booking's price, with their own commission rule. */}
        <Field label='Optional Services' className='tw:sm:col-span-2'>
          <OptionalServicesField
            value={form.optionalServices}
            onPatch={patchService}
            onAdd={addService}
            onRemove={removeService}
          />
        </Field>
      </FormSection>

      {/* Availability and closures. Two halves of one question — when this can be
          run — and one card, so an admin setting up an artisan's diary is not
          reading two cards with almost the same name.

          The calendar half appears only for the one booking type that has one:
          an inquiry is worked the other way round, so times listed there would be
          availability nobody has agreed to. Closures apply to every booking type,
          and matter most on an inquiry — that is the form where a concierge picks
          a date nobody has checked yet. */}
      <FormSection title='Availability and Closures'>
        {isCalendarBookingType(form.bookingType) && (
          <div className='tw:sm:col-span-2 tw:space-y-4'>
            <p className='tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase'>
              Times on offer
            </p>
            {/* A direct booking is taken at the published price — nobody
                negotiates one — so the server refuses to book an experience with
                no price. Said here because this is the screen where it is fixed. */}
            {form.basePrice <= 0 && (
              <p className='tw:rounded-md tw:border tw:border-destructive/30 tw:bg-destructive/5 tw:px-3 tw:py-2 tw:text-sm'>
                This experience has no base price, so nothing can be booked from the calendar. Set a base
                price above, or use an inquiry booking type instead.
              </p>
            )}
            {isEdit && experienceId ? (
              <AvailabilitySlotsField experienceId={experienceId} closures={closures} />
            ) : (
              // Slots are documents pointing at this experience, so there is
              // nothing to point them at until it exists. Said rather than shown
              // as a disabled form, which would read as broken.
              <p className='tw:text-sm tw:text-muted-foreground'>
                Create the experience first, then add the times the artisan has agreed to work.
              </p>
            )}
          </div>
        )}

        {/* Who runs it, which is what decides whose absences this experience
            inherits. In this card rather than under Content because that is the
            only thing the link currently does: a supplier's days away close every
            experience pointing at it, so a tea house shutting for a week is typed
            once instead of into each of its experiences. */}
        <Field label='Supplier' className='tw:sm:col-span-2'>
          <Select
            value={form.supplierId ?? 'none'}
            onValueChange={(v) =>
              commit('supplierId', v === 'none' ? null : v, 'supplier', supplierNameFormatter)}
          >
            <SelectTrigger>
              <SelectValue placeholder='No supplier' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='none'>No supplier</SelectItem>
              {suppliers.map((sup) => (
                <SelectItem key={sup.id} value={sup.id}>
                  {sup.name || sup.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            Optional. Linking one means this experience is also closed on the days recorded against that
            supplier, without them being copied here.
          </p>
        </Field>

        <div className='tw:sm:col-span-2'>
          <AvailabilityClosuresField
            periods={form.closurePeriods}
            weekdays={form.weekdayClosures}
            today={todayCalendarDate()}
            inherited={
              supplier
                ? {
                    periods: normaliseSupplierClosures(supplier),
                    sourceName: supplier.name || supplier.id,
                    sourceHref: `/admin/suppliers/${supplier.id}/edit`,
                  }
                : undefined
            }
            onChangePeriods={(v) => commit('closurePeriods', v, 'closures', countFormatter('closed period'))}
            onChangeWeekdays={(v) =>
              commit('weekdayClosures', v, 'weekly closures', countFormatter('closed weekday'))}
          />
        </div>
      </FormSection>

      {/* Media */}
      <FormSection title='Media'>
        <Field label='Hero Image' className='tw:sm:col-span-2'>
          <HeroImageField value={form.heroImage} onChange={(url) => commit('heroImage', url, 'hero image', () => null)} />
        </Field>
        <Field label='Hero Video' className='tw:sm:col-span-2'>
          {/* Formatted to nothing, as the hero image is: an uploaded video's
              download URL is a wall of characters that says nothing useful in
              an undo message. */}
          <HeroVideoField value={form.videoUrl} onChange={(url) => commit('videoUrl', url, 'hero video', () => null)} />
        </Field>
        <Field label='Gallery Images' className='tw:sm:col-span-2'>
          <GalleryImagesField
            value={form.galleryImages}
            onChange={(urls) => commit('galleryImages', urls, 'gallery images', countFormatter('image'))}
          />
        </Field>
        
      </FormSection>

      {/* Content */}
      <FormSection title='Content'>
        <Field label='Short Description' required className='tw:sm:col-span-2'>
          <AutosaveTextInput
            multiline
            rows={2}
            currentValue={form.shortDescription}
            onCommitChange={(v) => commit('shortDescription', v, 'short description')}
          />
        </Field>
        <Field label='Full Description' className='tw:sm:col-span-2'>
          <AutosaveRichTextInput
            currentValue={form.fullDescription}
            placeholder='The story of this experience…'
            onCommitChange={(v) => commit('fullDescription', v, 'full description')}
          />
        </Field>
        <Field label='Includes (one per line)' className='tw:sm:col-span-2'>
          <AutosaveTextInput
            multiline
            rows={4}
            currentValue={form.includes.join('\n')}
            onCommitChange={(v) => commit('includes', splitLines(v), 'includes', countFormatter('item'))}
          />
        </Field>
        <Field label='Closing Statement' className='tw:sm:col-span-2'>
          <AutosaveTextInput
            multiline
            rows={2}
            currentValue={form.closingStatement}
            onCommitChange={(v) => commit('closingStatement', v, 'closing statement')}
          />
        </Field>
      </FormSection>
    </div>
  );

  // ----- Edit mode: sticky autosave bar, no submit button -----
  if (isEdit) {
    return (
      <div className='tw:mx-auto tw:max-w-6xl'>
        <TopWhiteBarEditControls
          saveStack={saveStack}
          leftSlot={
            <Link
              to='/admin/experiences'
              className='tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground'
            >
              <ArrowLeft className='tw:size-4' /> Experiences
            </Link>
          }
          middleSlot={
            <UndoRedoButtons
              canUndo={undoRedo.canUndo}
              canRedo={undoRedo.canRedo}
              undoLabel={undoRedo.undoLabel}
              redoLabel={undoRedo.redoLabel}
              onUndo={() => undoRedo.undo((f, v, a) => applyChange(f, v, a, true))}
              onRedo={() => undoRedo.redo((f, v, a) => applyChange(f, v, a, true))}
            />
          }
        >
          <button
            type='button'
            onClick={() => setShowHistory(true)}
            className='tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-ed-stone tw:transition-colors tw:hover:text-ed-gold'
          >
            <History className='tw:size-4' /> History
            {!!form.history?.lastStep && <span className='tw:text-ed-stone/70'>({form.history.lastStep})</span>}
          </button>
        </TopWhiteBarEditControls>
        <header className='tw:mb-8'>
          <h1 className='tw:font-serif tw:text-3xl tw:font-light'>Edit — {form.name}</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-muted-foreground'>Changes save automatically.</p>
        </header>
        {fields}
        <HistoryPanel
          key={experienceId}
          show={showHistory}
          onClose={() => setShowHistory(false)}
          history={form.history}
          historyCollectionName={coll_experiencesHistory}
        />
      </div>
    );
  }

  // ----- New mode: create-then-navigate, no autosave -----
  return (
    <form onSubmit={handleCreate} className='tw:mx-auto tw:max-w-6xl'>
      <header className='tw:mb-8'>
        <Link
          to='/admin/experiences'
          className='tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground'
        >
          <ArrowLeft className='tw:size-4' /> Experiences
        </Link>
        <h1 className='tw:mt-2 tw:font-serif tw:text-3xl tw:font-light'>Add New Experience</h1>
      </header>

      {fields}

      {createError && (
        <p className='tw:mt-6 tw:text-right tw:text-sm tw:text-destructive'>{createError}</p>
      )}

      <div className='tw:mt-8 tw:flex tw:justify-end tw:gap-3'>
        <Button asChild variant='outline' type='button'>
          <Link to='/admin/experiences'>Cancel</Link>
        </Button>
        <Button type='submit' disabled={!form.name.trim()} loading={saving}>
          {saving ? 'Saving…' : 'Create Experience'}
        </Button>
      </div>
    </form>
  );
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}
